import { useCallback, useMemo, useState } from "react";
import { auth } from "../lib/firebase";
import { authenticatedFetch } from "../lib/api";
import { BusinessMember, BusinessInvitation, BusinessCustomSite, LoadStatus } from "../types";
import { RosterOrg } from "../lib/accountDeletion";
import { errorWithRef, fallbackError } from "../lib/apiError";

export function useBusinessTeam() {
  const [businessMembers, setBusinessMembers] = useState<BusinessMember[]>([]);
  const [businessInvitations, setBusinessInvitations] = useState<BusinessInvitation[]>([]);
  // True only once a roster fetch has SUCCEEDED, so the UI can tell "still loading / failed" from
  // "this tenant really has no members".
  const [membersLoaded, setMembersLoaded] = useState(false);
  // ENG-3479 review: the last roster fetch failed. With `membersLoaded` it separates "still loading"
  // from "answered, with nothing known", which the Overview setup checklist must not wait on for ever.
  const [membersFailed, setMembersFailed] = useState(false);
  // ENG-3350: the last roster load's own failure message. `businessError` carries it too, and every
  // action's, so this is how Members & Seats tells the two apart.
  const [membersError, setMembersError] = useState<string | null>(null);
  // ENG-3341: the org this roster describes, as the response names it. Settings reads it to tell an
  // owner their org still has members; `/subscription-status` can describe a different org for
  // someone in several, so the roster's own name is the only one that matches its count.
  const [rosterOrg, setRosterOrg] = useState<RosterOrg | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitingMember, setInvitingMember] = useState(false);
  const [businessError, setBusinessError] = useState<string | null>(null);
  const [businessSuccess, setBusinessSuccess] = useState<string | null>(null);

  const [businessCustomSites, setBusinessCustomSitesList] = useState<BusinessCustomSite[]>([]);
  // ENG-3342: a failed /business/custom-sites left this empty, and Secure Sites told the member the
  // organization had "No custom sites yet".
  const [businessCustomSitesStatus, setBusinessCustomSitesStatus] = useState<LoadStatus>("loading");
  const [addingBusinessSite, setAddingBusinessSite] = useState(false);
  const [businessSiteError, setBusinessSiteError] = useState<string | null>(null);

  /**
   * Load the roster. A failure MUST surface: this list backs Members & Seats, the group-members
   * dialog, and the Overview → detail email correlation, so swallowing a 403/500 rendered an empty
   * roster as "you have no members" on three surfaces at once.
   */
  const fetchBusinessMembers = useCallback(async () => {
    if (!auth.currentUser) return;
    try {
      const response = await authenticatedFetch('/tenant/members');
      if (response.ok) {
        const data = await response.json();
        setBusinessMembers(data.members || []);
        setBusinessInvitations(data.pendingInvitations || []);
        setRosterOrg(typeof data.tenantId === 'string' && typeof data.tenantName === 'string'
          ? { tenantId: data.tenantId, tenantName: data.tenantName }
          : null);
        setMembersLoaded(true);
        setMembersFailed(false);
        setMembersError(null);
        setBusinessError(null);
      } else {
        const data = await response.json().catch(() => ({}));
        // ENG-3341: the members above stay for the pages that show them, but the org they describe
        // is no longer known to be current. The common way here is an owner who handed the org over
        // and deactivated themselves — their stale "owner" row must not keep blocking the deletion
        // they did that for. The server's own check still stands behind this.
        setRosterOrg(null);
        setMembersFailed(true);
        const message = errorWithRef(data, () => fallbackError("Could not load your organization's members.", response));
        setMembersError(message);
        setBusinessError(message);
      }
    } catch (err) {
      console.error('Error fetching business members:', err);
      setRosterOrg(null);
      setMembersFailed(true);
      const message = err instanceof Error ? err.message : "Could not load your organization's members.";
      setMembersError(message);
      setBusinessError(message);
    }
  }, []);

  /**
   * ENG-3350: Try again after the roster failed to load. It is fetched once, at sign-in (App.tsx), so
   * nothing else would ask again. Clears the failure first, so the page shows it loading meanwhile.
   */
  const retryBusinessMembers = useCallback(() => {
    setMembersFailed(false);
    setMembersError(null);
    setBusinessError(null);
    return fetchBusinessMembers();
  }, [fetchBusinessMembers]);

  /**
   * The org's custom sites, from whoever has them. App.tsx seeds this from the /subscription-status
   * payload, which carries the same list, so an answer from there counts as loaded (ENG-3342).
   */
  const setBusinessCustomSites = useCallback((sites: BusinessCustomSite[]) => {
    setBusinessCustomSitesList(sites);
    setBusinessCustomSitesStatus("loaded");
  }, []);

  const fetchBusinessCustomSites = useCallback(async () => {
    if (!auth.currentUser) return;
    setBusinessCustomSitesStatus((s) => (s === "loaded" ? s : "loading"));
    try {
      const response = await authenticatedFetch('/business/custom-sites');
      if (!response.ok) throw new Error(`/business/custom-sites answered ${response.status}`);
      setBusinessCustomSites(await response.json());
    } catch (err) {
      console.error('Error fetching business custom sites:', err);
      // A list already shown (the seed, or an earlier load) stays; only nothing-to-show is an error.
      setBusinessCustomSitesStatus((s) => (s === "loaded" ? s : "error"));
    }
  }, [setBusinessCustomSites]);

  const handleSendInvite = useCallback(async (email?: string, role?: string, name?: string): Promise<{ success: boolean }> => {
    const targetEmail = email || inviteEmail;
    if (!targetEmail.trim()) return { success: false };
    setInvitingMember(true);
    setBusinessError(null);
    try {
      const body: Record<string, string> = { email: targetEmail.trim(), role: role || 'member' };
      if (name) body.name = name;
      const response = await authenticatedFetch('/tenant/invite', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (response.ok) {
        setInviteEmail('');
        fetchBusinessMembers();
        return { success: true };
      } else {
        setBusinessError(data.error || 'Failed to send invite');
        return { success: false };
      }
    } catch (err: unknown) {
      setBusinessError(err instanceof Error ? err.message : 'Failed to send invite');
      return { success: false };
    } finally {
      setInvitingMember(false);
    }
  }, [fetchBusinessMembers, inviteEmail]);

  // ENG-3351: the error is returned as well as set, so the confirmation that asked for the change can
  // say why it was refused. The member detail view shows no `businessError`.
  // ENG-3853: [managementOnly] also sets "not tracked"; left out of the body when absent, so the server
  // leaves it as it is.
  const handleChangeRole = useCallback(async (tenantUserId: string, newRole: string, managementOnly?: boolean): Promise<{ success: boolean; error?: string }> => {
    setBusinessError(null);
    try {
      const response = await authenticatedFetch(`/tenant/members/${tenantUserId}/role`, {
        method: 'PATCH',
        body: JSON.stringify(managementOnly === undefined ? { role: newRole } : { role: newRole, managementOnly }),
      });
      if (response.ok) {
        fetchBusinessMembers();
        // ENG-4089: on a family, a promotion is a request the member answers (202). Said, so the Owner
        // does not read the unchanged Member chip as the change having failed.
        if (response.status === 202) {
          setBusinessSuccess('Request sent. Their access stays Member unless they say yes.');
          return { success: true };
        }
        // ENG-3853: a server from before "not tracked" ignores the field and answers 200 having changed
        // only the role, so say what did not happen rather than closing the dialog as if it had. The
        // flag is omitted from the response when false.
        if (managementOnly !== undefined) {
          const data = await response.json().catch(() => ({}));
          if (!!data.isManagementOnly !== managementOnly) {
            const msg = 'Their role was changed, but whether they are tracked was not. Haven may still be updating; try again later.';
            setBusinessError(msg);
            return { success: false, error: msg };
          }
        }
        return { success: true };
      } else {
        const data = await response.json().catch(() => ({}));
        const msg = data.error || 'Failed to change role';
        setBusinessError(msg);
        return { success: false, error: msg };
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to change role';
      setBusinessError(msg);
      return { success: false, error: msg };
    }
  }, [fetchBusinessMembers]);

  /**
   * ENG-2983: hand ownership to another member.
   *
   * Owner-only server-side, and the server re-reads the caller's role in the tenant being acted on
   * — this hook cannot grant anything the caller does not already have. `disposition` is passed
   * explicitly rather than defaulted, because the caller is deciding it about themselves.
   */
  const handleTransferOwnership = useCallback(async (
    successorTenantUserId: string,
    disposition: 'demote_admin' | 'demote_member' | 'deactivate',
  ): Promise<{ success: boolean; error?: string }> => {
    setBusinessError(null);
    try {
      const response = await authenticatedFetch('/tenant/transfer-ownership', {
        method: 'POST',
        body: JSON.stringify({ successorTenantUserId, disposition }),
      });
      if (response.ok) {
        fetchBusinessMembers();
        return { success: true };
      }
      const data = await response.json();
      // The server's refusal text is written for the person reading it, so it is surfaced verbatim
      // rather than replaced with a generic failure.
      const message = data.error || 'Could not transfer ownership.';
      setBusinessError(message);
      return { success: false, error: message };
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Could not transfer ownership.';
      setBusinessError(message);
      return { success: false, error: message };
    }
  }, [fetchBusinessMembers]);

  // ENG-2491: edit a member's display name in place (no delete-and-re-add).
  const handleEditMemberName = useCallback(async (tenantUserId: string, name: string): Promise<{ success: boolean; error?: string }> => {
    setBusinessError(null);
    if (!name.trim()) return { success: false, error: 'Name cannot be empty' };
    try {
      const response = await authenticatedFetch(`/tenant/members/${tenantUserId}`, {
        method: 'PATCH',
        body: JSON.stringify({ name: name.trim() }),
      });
      if (response.ok) {
        fetchBusinessMembers();
        return { success: true };
      }
      const data = await response.json();
      setBusinessError(data.error || 'Failed to update member');
      return { success: false, error: data.error };
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to update member';
      setBusinessError(msg);
      return { success: false, error: msg };
    }
  }, [fetchBusinessMembers]);

  // ENG-2491: correct a mis-typed email on a pending invitation (cancel + reissue). ENG-3339: with the
  // email unchanged, a new name is edited in place instead - same link, no new email.
  const handleCorrectInvitationEmail = useCallback(async (invitationId: string, email: string, name?: string): Promise<{ success: boolean; error?: string }> => {
    setBusinessError(null);
    if (!email.trim()) return { success: false, error: 'Email cannot be empty' };
    try {
      const response = await authenticatedFetch(`/tenant/invitations/${invitationId}`, {
        method: 'PATCH',
        body: JSON.stringify({ email: email.trim(), ...(name?.trim() ? { name: name.trim() } : {}) }),
      });
      if (response.ok) {
        fetchBusinessMembers();
        return { success: true };
      }
      const data = await response.json();
      setBusinessError(data.error || 'Failed to correct invitation');
      return { success: false, error: data.error };
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to correct invitation';
      setBusinessError(msg);
      return { success: false, error: msg };
    }
  }, [fetchBusinessMembers]);

  /**
   * ENG-2759 — change the access a still-pending invitation will grant.
   *
   * The member equivalent (`handleChangeRole`) has always existed; invitations had no way to correct
   * a role short of revoking and re-inviting, which is the dead end a customer hit after inviting two
   * people as Admin. Same PATCH the email correction uses, with the email unchanged.
   *
   * ENG-3339: the backend refused an unchanged email as "already the invited email" before it read
   * the role, so this never once succeeded. It now edits the invitation in place: the link the
   * invitee already has keeps working and grants the new access, and no second email is sent.
   */
  const handleChangeInvitationRole = useCallback(async (invitationId: string, email: string, newRole: string): Promise<{ success: boolean; error?: string }> => {
    setBusinessError(null);
    try {
      const response = await authenticatedFetch(`/tenant/invitations/${invitationId}`, {
        method: 'PATCH',
        // The email is required by the endpoint and is unchanged here — this call only moves the role.
        body: JSON.stringify({ email, role: newRole }),
      });
      if (response.ok) {
        fetchBusinessMembers();
        return { success: true };
      }
      const data = await response.json();
      setBusinessError(data.error || 'Failed to change invitation role');
      return { success: false, error: data.error };
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to change invitation role';
      setBusinessError(msg);
      return { success: false, error: msg };
    }
  }, [fetchBusinessMembers]);

  // ENG-4089: the family Owner takes back an admin request the member has not answered.
  const handleWithdrawAdminRequest = useCallback(async (tenantUserId: string): Promise<{ success: boolean; error?: string }> => {
    setBusinessError(null);
    try {
      const response = await authenticatedFetch(`/tenant/members/${tenantUserId}/admin-appointment`, { method: 'DELETE' });
      fetchBusinessMembers();
      if (response.ok) return { success: true };
      const data = await response.json().catch(() => ({}));
      return { success: false, error: data.error || 'Could not withdraw the request.' };
    } catch (err: unknown) {
      return { success: false, error: err instanceof Error ? err.message : 'Could not withdraw the request.' };
    }
  }, [fetchBusinessMembers]);

  // ENG-3351: returns its error for the same reason as handleChangeRole.
  const handleRemoveMember = useCallback(async (tenantUserId: string): Promise<{ success: boolean; error?: string }> => {
    setBusinessError(null);
    try {
      const response = await authenticatedFetch(`/tenant/members/${tenantUserId}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        fetchBusinessMembers();
        return { success: true };
      } else {
        const data = await response.json().catch(() => ({}));
        const msg = data.error || 'Failed to remove member';
        setBusinessError(msg);
        return { success: false, error: msg };
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to remove member';
      setBusinessError(msg);
      return { success: false, error: msg };
    }
  }, [fetchBusinessMembers]);

  /**
   * ENG-3339: a refused revoke is surfaced and reported. This used to act only on success, so a
   * failed revoke left the row in place with nothing on screen.
   *
   * ENG-3896: a 409 means the invitation was accepted before the revoke reached it, so the row on
   * screen is stale: the invitee is a member now. The roster is read again so it leaves the pending
   * list, and the server's message is set after that read, which would otherwise clear it.
   */
  const handleRevokeInvitation = useCallback(async (invitationId: string): Promise<{ success: boolean; error?: string }> => {
    setBusinessError(null);
    try {
      const response = await authenticatedFetch(`/tenant/invitations/${invitationId}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        fetchBusinessMembers();
        return { success: true };
      }
      const data = await response.json().catch(() => ({}));
      const msg = data.error || 'Failed to revoke invitation';
      if (response.status === 409) await fetchBusinessMembers();
      setBusinessError(msg);
      return { success: false, error: msg };
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to revoke invitation';
      setBusinessError(msg);
      return { success: false, error: msg };
    }
  }, [fetchBusinessMembers]);

  /**
   * ENG-3339 — send a fresh link to the same person, with the SAME access and name.
   *
   * ENG-3371: one request. This was two - revoke, then re-invite through POST /tenant/invite - so
   * when the re-invite failed the invitation was already gone, and the error never said its link had
   * stopped working. The server now swaps the old invitation for the new one in a single write,
   * copying the access and name itself, so a failure leaves the invitation as it was. It also means
   * one roster read after the change instead of two racing ones, the earlier of which could land
   * last and clear the re-invite's error off the screen.
   */
  const handleResendInvitation = useCallback(async (
    invitation: Pick<BusinessInvitation, 'id'>,
  ): Promise<{ success: boolean; error?: string }> => {
    setBusinessError(null);
    try {
      const response = await authenticatedFetch(`/tenant/invitations/${invitation.id}/resend`, {
        method: 'POST',
      });
      if (response.ok) {
        fetchBusinessMembers();
        return { success: true };
      }
      const data = await response.json().catch(() => ({}));
      const msg = data.error || 'Failed to resend invitation';
      setBusinessError(msg);
      return { success: false, error: msg };
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to resend invitation';
      setBusinessError(msg);
      return { success: false, error: msg };
    }
  }, [fetchBusinessMembers]);

  const handleAddBusinessCustomSite = useCallback(async (
    name: string,
    url: string
  ): Promise<{ success: boolean }> => {
    if (!name.trim() || !url.trim()) return { success: false };
    setAddingBusinessSite(true);
    setBusinessSiteError(null);
    try {
      const response = await authenticatedFetch('/business/custom-sites', {
        method: 'POST',
        body: JSON.stringify({ title: name.trim(), launchUrl: url.trim() }),
      });
      const data = await response.json();
      if (response.ok) {
        fetchBusinessCustomSites();
        return { success: true };
      } else {
        setBusinessSiteError(data.error || 'Failed to add site');
        return { success: false };
      }
    } catch (err: unknown) {
      setBusinessSiteError(err instanceof Error ? err.message : 'Failed to add site');
      return { success: false };
    } finally {
      setAddingBusinessSite(false);
    }
  }, [fetchBusinessCustomSites]);

  // ENG-3351: a refusal carries the server's reason into the confirmation that asked. Without one the
  // dialog says the site is still protected, which a 404 (already removed) would make false.
  const handleRemoveBusinessCustomSite = useCallback(async (siteId: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await authenticatedFetch(`/business/custom-sites/${siteId}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        fetchBusinessCustomSites();
        return { success: true };
      }
      const data = await response.json().catch(() => ({}));
      return { success: false, error: data.error || data.message };
    } catch (err) {
      console.error('Error removing business custom site:', err);
      return { success: false };
    }
  }, [fetchBusinessCustomSites]);

  const handleAddAlias = useCallback(async (
    tenantUserId: string,
    aliasEmail: string
  ): Promise<{ success: boolean; error?: string }> => {
    if (!aliasEmail.trim()) return { success: false, error: 'Enter an email address' };
    try {
      const response = await authenticatedFetch(`/business/members/${tenantUserId}/aliases`, {
        method: 'POST',
        body: JSON.stringify({ aliasEmail: aliasEmail.trim() }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        await fetchBusinessMembers();
        return { success: true };
      }
      return { success: false, error: data.error || 'Failed to add alias' };
    } catch (err: unknown) {
      return { success: false, error: err instanceof Error ? err.message : 'Failed to add alias' };
    }
  }, [fetchBusinessMembers]);

  const handleRemoveAlias = useCallback(async (
    tenantUserId: string,
    aliasEmail: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await authenticatedFetch(`/business/members/${tenantUserId}/aliases`, {
        method: 'DELETE',
        body: JSON.stringify({ aliasEmail }),
      });
      if (response.ok) {
        await fetchBusinessMembers();
        return { success: true };
      }
      const data = await response.json().catch(() => ({}));
      return { success: false, error: data.error || 'Failed to remove alias' };
    } catch (err: unknown) {
      return { success: false, error: err instanceof Error ? err.message : 'Failed to remove alias' };
    }
  }, [fetchBusinessMembers]);

  // Clear all team state on logout / user-switch so it can't leak to the next user.
  const reset = useCallback(() => {
    setBusinessMembers([]);
    setBusinessInvitations([]);
    setBusinessCustomSitesList([]);
    setBusinessCustomSitesStatus("loading");
    setMembersLoaded(false);
    setMembersFailed(false);
    setMembersError(null);
    setRosterOrg(null);
    setBusinessError(null);
    setBusinessSuccess(null);
  }, []);

  // ENG-2722: memoised alongside the callbacks — a consumer that keys an effect on this object
  // (UserDetailView does, via onFetchMembers) otherwise refetches once per render of the parent.
  return useMemo(() => ({
    reset,
    businessMembers,
    businessInvitations,
    membersLoaded,
    membersFailed,
    membersError,
    rosterOrg,
    inviteEmail,
    setInviteEmail,
    invitingMember,
    businessError,
    setBusinessError,
    businessSuccess,
    setBusinessSuccess,
    businessCustomSites,
    businessCustomSitesStatus,
    setBusinessCustomSites,
    addingBusinessSite,
    businessSiteError,
    setBusinessSiteError,
    fetchBusinessMembers,
    retryBusinessMembers,
    fetchBusinessCustomSites,
    handleSendInvite,
    handleChangeRole,
    handleTransferOwnership,
    handleEditMemberName,
    handleCorrectInvitationEmail,
    handleChangeInvitationRole,
    handleRemoveMember,
    handleWithdrawAdminRequest,
    handleRevokeInvitation,
    handleResendInvitation,
    handleAddBusinessCustomSite,
    handleRemoveBusinessCustomSite,
    handleAddAlias,
    handleRemoveAlias,
  }), [
    reset, businessMembers, businessInvitations, membersLoaded, membersFailed, membersError, rosterOrg, inviteEmail, invitingMember,
    businessError, businessSuccess, businessCustomSites, businessCustomSitesStatus, setBusinessCustomSites,
    addingBusinessSite, businessSiteError,
    fetchBusinessMembers, retryBusinessMembers, fetchBusinessCustomSites, handleSendInvite, handleChangeRole,
    handleTransferOwnership,
    handleEditMemberName, handleCorrectInvitationEmail, handleChangeInvitationRole,
    handleRemoveMember, handleWithdrawAdminRequest, handleRevokeInvitation, handleResendInvitation, handleAddBusinessCustomSite,
    handleRemoveBusinessCustomSite, handleAddAlias, handleRemoveAlias,
  ]);
}
